<script lang="ts">
  import {formatCount, Qty} from '@exomath/common';
  import {Button, Checkbox, Icon, Input, Logo} from '$components';
  import Alert from '../Alert.svelte';

  const DURATION_UNITS = [
    {unit: 'day',    seconds: Qty.Time.s.from.day(1)},
    {unit: 'hour',   seconds: Qty.Time.s.from.hr(1)},
    {unit: 'minute', seconds: Qty.Time.s.from.min(1)},
    {unit: 'second', seconds: Qty.Time.s.from.s(1)}
  ];

  /**
   * If online sessions are enabled, use the offline session idle. Otherwise, use the greater of
   * the SSO session idle and the SSO session idle remember me. Not all of these values are
   * available as Keycloak properties, which is why this must be kept in sync manually.
   */
  const REMEMBER_ME_LIFESPAN = Qty.Time.s.from.day(30);

  interface Props {
    insideIFrame?: boolean;
  }

  const {insideIFrame = false}: Props = $props();

  const alertMessage = $derived(!messagesPerField.exists('username', 'password')
    && message && (message.type !== 'warning' || !auth.isAppInitiatedAction)
    && !insideIFrame ? message : null);

  let showPassword = $state(false);

  function formatDuration(totalSeconds: number) {
    const largest = DURATION_UNITS.find(({seconds}) => totalSeconds >= seconds && totalSeconds % seconds === 0)
      ?? DURATION_UNITS[DURATION_UNITS.length - 1];

    return formatCount(totalSeconds / largest.seconds, largest.unit);
  }
</script>

<div class="flex flex-col justify-start items-center w-82.5 xs:w-100 px-4 py-6 sm:py-12">
  <div class="flex flex-col justify-start items-center w-full">
    {#if !insideIFrame}
      <a href={properties.siteHref} class="w-full">
        <Logo width="full"/>
      </a>
    {/if}

    <div class="mt-6 xs:mt-10">
      {#if insideIFrame || !auth.showUsername || auth.showResetCredentials}
        <h2 class="text-2xl/9 font-bold tracking-tight">
          {#if insideIFrame}
            {@html msg('reauthenticate')}
          {:else}
            {@html msg('loginAccountTitle')}
          {/if}
        </h2>
      {:else}
        <div class="flex flex-row justify-center items-center gap-2">
          <span class="font-semibold">
            {auth.attemptedUsername}
          </span>
        
          <a
            href={url.loginRestartFlowUrl}
            title={msg('restartLoginTooltip')}
            class="inline-flex font-semibold text-primary hover:text-primary-dark"
          >
            <Icon name="external-link" style="solid" size={5}/>
          </a>
        </div>
      {/if}
    </div>

    {#if !insideIFrame && realm.password && realm.registrationAllowed && !login.registrationDisabled}
      <div class="mt-2 text-center text-sm text-secondary-800">
        {@html msg('noAccount')}

        <a href={url.registrationUrl} class="font-semibold text-primary hover:text-primary-dark">
          {@html msg('goRegister')}
        </a>
      </div>
    {/if}

    <div class="w-full mt-2 sm:mt-4">
      <div class="flex flex-row justify-center items-center text-sm font-medium">
        {#if alertMessage}
          <Alert type={alertMessage.type}>
            {@html alertMessage.unescapedSummary}
          </Alert>
        {/if}
      </div>

      {#if realm.password}
        <form
          class="mt-2 space-y-6"
          action={url.loginAction}
          method="POST"
          onsubmit={() => { login.disabled = true; }}
        >
          {#if !auth.usernameHidden}
            <div class="pt-2">
              <Input
                name="username"
                value={login.username || ''}
                label={login.usernameLabel}
                error={messagesPerField.getFirst('username', 'password')}
                autocomplete={realm.loginWithEmailAllowed ? 'email' : 'username'}
                floatingLabel
                required
                autofocus
              />
            </div>
          {/if}

          <div class="pt-2">
            <Input
              name="password"
              type={showPassword ? 'text' : 'password'}
              label={msg('password')}
              suffix={{
                icon: {
                  name: showPassword ? 'hide' : 'eye',
                  caption: showPassword ? 'Hide password' : 'Show password',
                  onClick: () => { showPassword = !showPassword; }
                }
              }}
              error={auth.usernameHidden && messagesPerField.exists('username', 'password') ? messagesPerField.getFirst('username', 'password') : ''}
              floatingLabel
              required
            />
          </div>

          <div class="flex flex-col justify-start items-start gap-4 text-sm">
            {#if realm.resetPasswordAllowed}
              <a href={url.loginResetCredentialsUrl} class="font-semibold text-primary hover:text-primary-dark">
                {@html msg('doForgotPassword')}
              </a>
            {/if}
            
            {#if !insideIFrame && realm.rememberMe && !auth.usernameHidden}
              <Checkbox
                name="rememberMe"
                checked={login.rememberMe}
                label={msg('rememberMe', formatDuration(REMEMBER_ME_LIFESPAN))}
              />
            {/if}
          </div>

          <Button name="login" type="submit" label={msg('doLogIn')} fullWidth/>

          <input
            name="credentialId"
            type="hidden"
            value={auth.selectedCredential ?? ''}
          />
        </form>
      {/if}

      {#if auth.showTryAnotherWayLink}
        <form action={url.loginAction} method="POST">
          <input name="tryAnotherWay" type="hidden" value="on"/>
          <Button type="submit" color="secondary" label={msg('doTryAnotherWay')} fullWidth/>
        </form>
      {/if}

      {#if realm.password && social.providers.length}
        <div class="mt-6 separate text-sm/6 font-medium text-secondary-900">
          {@html msg('identity-provider-login-label')}
        </div>

        <div class="mt-6 grid grid-cols-1 gap-3">
          {#each social.providers as provider}
            <a
              type="button"
              href={provider.loginUrl}
              data-provider={provider.alias}
              class="flex flex-row justify-center items-center w-full shadow-xs border rounded-md border-secondary-300 px-4 py-2 bg-white hover:bg-secondary-50"
            >
              <Icon name="{provider.alias}-logo" style="color" size={5}/>
        
              <span class="ml-2.5 text-base font-medium text-secondary-600">
                {provider.displayName}
              </span>
            </a>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>
